/**
 * Data exports: scheduled jobs that push the network's data out to an
 * external system. The advertising application offers two sources — the ad
 * server's raw logs, sent to a storage bucket, and daily stats, sent to a
 * bucket or a Google sheet. (The Data application's segment and profile
 * exports are not offered here.)
 *
 * Secrets — keys, passwords and credentials — come back masked with `*`. The
 * API ignores a masked value sent back unchanged, so the settings can be
 * posted as they were loaded without wiping a secret.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { getContext } from '@/services/networks/networksApi'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** `DataExport.Source` in core, limited to the advertising application's. */
export const DATA_EXPORT_SOURCES = ['STATS', 'RAW_LOG'] as const
export type DataExportSource = (typeof DATA_EXPORT_SOURCES)[number]

/** `DataExport.Type` in core: where the data is sent. */
export type DataExportType =
  | 'S3'
  | 'S3_COMPATIBLE_BUCKET'
  | 'GCS'
  | 'AZURE_STORAGE_BLOB'
  | 'SHEET'
  | 'SFTP'
  | 'WEB_HOOK'
  | 'PLATFORM_161'
  | 'IN_MEMORY'

/** The destinations each source can be sent to. */
export const DATA_EXPORT_TYPES: Record<DataExportSource, DataExportType[]> = {
  STATS: ['SHEET', 'S3', 'S3_COMPATIBLE_BUCKET', 'GCS', 'AZURE_STORAGE_BLOB'],
  RAW_LOG: ['S3', 'S3_COMPATIBLE_BUCKET', 'GCS', 'AZURE_STORAGE_BLOB'],
}

export type DataExportFormat = 'CSV' | 'JSON' | 'PARQUET'
/** Stats files can be CSV or Parquet; raw logs have a fixed format. */
export const STATS_FORMATS: DataExportFormat[] = ['CSV', 'PARQUET']

export type DataExportSchedule = 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY'
/** Raw logs go out hourly or daily; stats always go out daily. */
export const RAW_LOG_SCHEDULES: DataExportSchedule[] = ['HOURLY', 'DAILY']

/** `ng.model.truth.EventType` values a raw log export can be limited to. */
export const DATA_EXPORT_EVENT_TYPES = [
  'IMPRESSION',
  'IN_BROWSER',
  'VISIBLE',
  'VIEWABLE',
  'CLICK',
  'CUSTOM',
] as const

/** `StatsSettings.StatsField` in core, in the order the export writes them. */
export const STATS_FIELDS = [
  'Start',
  'StartDate',
  'Publisher',
  'Publisher_Id',
  'Site',
  'Site_Id',
  'Ad_Unit',
  'Ad_Unit_Id',
  'Ad_Unit_Labels',
  'Advertiser',
  'Advertiser_Id',
  'Order',
  'OrderName',
  'Order_Id',
  'Order_Team',
  'Order_Team_Id',
  'Line_Item',
  'Line_Item_Id',
  'Political',
  'Creative',
  'Creative_Id',
  'Creative_Source',
  'Creative_Size',
  'Available_Traffic',
  'Losing_Bids',
  'Impressions',
  'Rendered_Impressions',
  'Visibles',
  'Visibility',
  'Viewables',
  'Viewability',
  'Clicks',
  'Conversions',
  'CTR',
  'Currency',
  'eCPM',
  'vCPM',
  'Cost',
  'Total_Cost',
] as const

/** The stats fields a network offers; `Publisher` only exists on marketplaces. */
export function availableStatsFields(marketplace: boolean): string[] {
  return STATS_FIELDS.filter((field) => marketplace || field !== 'Publisher')
}

/** `StatsSettings.FilterType` in core; null exports the whole network. */
export type StatsFilterType = 'TEAM' | 'ADVERTISER' | 'ORDER'
export const STATS_FILTER_TYPES: StatsFilterType[] = ['TEAM', 'ADVERTISER', 'ORDER']

/** `Regions` in the AWS SDK, as core accepts them. */
export const AWS_REGIONS = [
  'EU_CENTRAL_1',
  'EU_CENTRAL_2',
  'EU_NORTH_1',
  'EU_SOUTH_1',
  'EU_SOUTH_2',
  'EU_WEST_1',
  'EU_WEST_2',
  'EU_WEST_3',
  'US_EAST_1',
  'US_EAST_2',
  'US_WEST_1',
  'US_WEST_2',
  'US_GOV_EAST_1',
  'GovCloud',
  'CA_CENTRAL_1',
  'CA_WEST_1',
  'SA_EAST_1',
  'AP_EAST_1',
  'AP_SOUTH_1',
  'AP_SOUTH_2',
  'AP_SOUTHEAST_1',
  'AP_SOUTHEAST_2',
  'AP_SOUTHEAST_3',
  'AP_SOUTHEAST_4',
  'AP_NORTHEAST_1',
  'AP_NORTHEAST_2',
  'AP_NORTHEAST_3',
  'CN_NORTH_1',
  'CN_NORTHWEST_1',
  'ME_CENTRAL_1',
  'ME_SOUTH_1',
  'AF_SOUTH_1',
  'IL_CENTRAL_1',
  'US_ISO_EAST_1',
  'US_ISOB_EAST_1',
  'US_ISO_WEST_1',
] as const

export interface ObjectRef {
  id: string
  name: string
}

export interface AwsSettings {
  endpoint: string
  accessKeyId: string
  secretKey: string
  region: string
  bucketName: string
  remoteDirectory: string
}

export interface GcsSettings {
  projectId: string
  credentialsJson: string
  bucketName: string
  remoteDirectory: string
}

export interface AzureSettings {
  servicePrincipalClientId: string
  servicePrincipalClientSecret: string
  tenantId: string
  endpoint: string
  container: string
  remoteDirectory: string
  environment: string
}

export interface SheetSettings {
  spreadsheetId: string
  sheetTitle: string
  credentialsJson: string
}

export interface StatsSettings {
  fields: string[]
  useLineItemCurrency: boolean
  currency: string
  filterType: StatsFilterType | null
  teams: ObjectRef[]
  advertisers: ObjectRef[]
  orders: ObjectRef[]
}

export interface DataExport {
  id: string
  name: string
  description: string
  source: DataExportSource | string
  type: DataExportType
  format: DataExportFormat | null
  schedule: DataExportSchedule | null
  /** Network-local time of day, `HH:mm:ss`; null runs at the time it was created. */
  scheduleTime: string | null
  /** Empty exports every event type. */
  eventTypes: string[]
  /** Raw log exports only: the teams whose data is exported. */
  teams: ObjectRef[]
  awsSettings: AwsSettings
  gcsSettings: GcsSettings
  azureSettings: AzureSettings
  sheetSettings: SheetSettings
  statsSettings: StatsSettings
  lastSuccessfulExport: string | null
  nextScheduledFor: string | null
  createUser: ObjectRef | null
  objectState: string
  createTime: string
  updateTime: string
  validationWarnings: ValidationWarning[]
}

export interface DataExportInput {
  name: string
  description?: string
  source?: DataExportSource
  type?: DataExportType
  format?: DataExportFormat | null
  data?: 'DELTAS' | 'ALL_RECORDS'
  compression?: 'NONE' | 'GZIP'
  schedule?: DataExportSchedule
  scheduleTime?: string | null
  eventTypes?: string[]
  teams?: string[]
  awsSettings?: Partial<AwsSettings> | null
  gcsSettings?: Partial<GcsSettings> | null
  azureSettings?: Partial<AzureSettings> | null
  sheetSettings?: Partial<SheetSettings> | null
  statsSettings?: {
    fields: string[]
    useLineItemCurrency: boolean
    currency: string | null
    filterType: StatsFilterType | null
    teams: string[]
    advertisers: string[]
    orders: string[]
  }
  objectState?: string
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

/** A reference the API sends as an id or as an `{ id, name }` object. */
function mapRefs(value: unknown): ObjectRef[] {
  if (!Array.isArray(value)) return []
  return value
    .map((v) =>
      typeof v === 'string'
        ? { id: v, name: '' }
        : { id: str((v as Record<string, unknown>)?.id), name: str((v as Record<string, unknown>)?.name) }
    )
    .filter((ref) => ref.id)
}

function mapDataExport(r: Record<string, unknown>): DataExport {
  const aws = (r.awsSettings ?? {}) as Record<string, unknown>
  const gcs = (r.gcsSettings ?? {}) as Record<string, unknown>
  const azure = (r.azureSettings ?? {}) as Record<string, unknown>
  const sheet = (r.sheetSettings ?? {}) as Record<string, unknown>
  const stats = (r.statsSettings ?? {}) as Record<string, unknown>
  const createUser = r.createUser as Record<string, unknown> | undefined
  return {
    id: str(r.id),
    name: str(r.name),
    description: str(r.description),
    source: str(r.source),
    type: (r.type as DataExportType) ?? 'S3',
    format: (r.format as DataExportFormat) ?? null,
    schedule: (r.schedule as DataExportSchedule) ?? null,
    scheduleTime: (r.scheduleTime as string) ?? null,
    eventTypes: Array.isArray(r.eventTypes) ? (r.eventTypes as string[]) : [],
    teams: mapRefs(r.teams),
    awsSettings: {
      endpoint: str(aws.endpoint),
      accessKeyId: str(aws.accessKeyId),
      secretKey: str(aws.secretKey),
      region: str(aws.region),
      bucketName: str(aws.bucketName),
      remoteDirectory: str(aws.remoteDirectory),
    },
    gcsSettings: {
      projectId: str(gcs.projectId),
      credentialsJson: str(gcs.credentialsJson),
      bucketName: str(gcs.bucketName),
      remoteDirectory: str(gcs.remoteDirectory),
    },
    azureSettings: {
      servicePrincipalClientId: str(azure.servicePrincipalClientId),
      servicePrincipalClientSecret: str(azure.servicePrincipalClientSecret),
      tenantId: str(azure.tenantId),
      endpoint: str(azure.endpoint),
      container: str(azure.container),
      remoteDirectory: str(azure.remoteDirectory),
      environment: str(azure.environment),
    },
    sheetSettings: {
      spreadsheetId: str(sheet.spreadsheetId),
      sheetTitle: str(sheet.sheetTitle),
      credentialsJson: str(sheet.credentialsJson),
    },
    statsSettings: {
      fields: Array.isArray(stats.fields) ? (stats.fields as string[]) : [],
      useLineItemCurrency: stats.useLineItemCurrency === true,
      currency: str(stats.currency),
      filterType: (stats.filterType as StatsFilterType) ?? null,
      teams: mapRefs(stats.teams),
      advertisers: mapRefs(stats.advertisers),
      orders: mapRefs(stats.orders),
    },
    lastSuccessfulExport: (r.lastSuccessfulExport as string) ?? null,
    nextScheduledFor: (r.nextScheduledFor as string) ?? null,
    createUser: createUser ? { id: str(createUser.id), name: str(createUser.name) } : null,
    objectState: str(r.objectState),
    createTime: str(r.createTime),
    updateTime: str(r.updateTime),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

export interface DataExportListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of data exports, narrowed by the given filters on the server. */
async function fetchDataExportPage(
  { search, status, sort }: DataExportListOptions,
  page: number,
  pageSize: number
): Promise<ListPage<DataExport>> {
  const params = new URLSearchParams({
    ...searchParams('dataExports', search),
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
    where: `source in ${DATA_EXPORT_SOURCES.join(',')}`,
  })
  const res = await apiFetch(`${API_BASE}/dataexports?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch data exports')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapDataExport)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const DataExportsApi = {
  getPage: ({ page = 1, pageSize = 25, ...filters }: DataExportListOptions = {}) =>
    fetchDataExportPage(filters, page, pageSize),

  getById: async (id: string): Promise<DataExport> => {
    const res = await apiFetch(`${API_BASE}/dataexports/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch data export')
    return mapDataExport(await res.json())
  },

  create: async (id: string, input: DataExportInput): Promise<DataExport> => {
    const res = await apiFetch(`${API_BASE}/dataexports/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create data export')
    return mapDataExport(await res.json())
  },

  update: async (id: string, input: DataExportInput): Promise<DataExport> => {
    const res = await apiFetch(`${API_BASE}/dataexports/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save data export')
    return mapDataExport(await res.json())
  },

  /** Copies an export, secrets included, under a new id. */
  copy: async (id: string, newId: string, name: string, description: string): Promise<DataExport> => {
    const params = new URLSearchParams({ context: getContext(), dataExportId: newId, name, description })
    const res = await apiFetch(`${API_BASE}/dataexports/${id}/copy?${params}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: '{}',
    })
    if (!res.ok) await throwApiError(res, 'Failed to copy data export')
    return mapDataExport(await res.json())
  },
}
